import type { Partner } from "./partner";
import type { Property } from "./property";
import type { Unit } from "./unit";
import type {
  ExecutionModes,
  Verification,
  VerificationNames
} from "./verification";

/**
 * Enum representing the various statuses of an application.
 *
 * This enum defines the different states an application can have throughout its lifecycle:
 *
 * - `Pending`: Represents applicants who have been invited but have not yet started the verification process.
 * - `Started`: Represents applicants who are in progress, still working on completing verifications.
 * - `Submitted`: Represents applications that have been fully submitted.
 * - `Approved`: Represents applications that have been approved by the partner after review.
 * - `Rejected`: Represents applications that have been rejected by the partner after review.
 * - `Canceled`: Represents applications that were canceled by the partner.
 * - `ConditionalApproved`: Represents applications that have been approved with conditions by the partner.
 * - `Merged`: Represents an internal status used when applications are merged (e.g., donor applications) and are no longer visible.
 * - `Processing`: Represents applicants who have checked out but are still undergoing asynchronous verification (e.g., waiting for a credit report).
 * - `PartiallySubmitted`: Represents partially submitted applications where some applicants have completed their submissions, but others are still working on their verifications.
 * - `OnHold`: Represent status that submitted application can be transitioned to. This block reports and further actions in applications. Kinda like freeze and save for later
 *
 */
export enum ApplicationStatus {
  /**
   * Represents applicants who have been invited but have not yet started the verification process.
   */
  Pending = "pending",

  /**
   * Represents applicants who are in progress, still working on completing verifications.
   */
  Started = "started",

  /**
   * Represents applications that have been fully submitted.
   */
  Submitted = "submitted",

  /**
   * Represents applications that have been approved by the partner after review.
   */
  Approved = "approved",

  /**
   * Represents applications that have been rejected by the partner after review.
   */
  Rejected = "rejected",

  /**
   * Represents applications that were canceled by the partner.
   */
  Canceled = "canceled",

  /**
   * Represents applications that have been approved with conditions by the partner.
   */
  ConditionalApproved = "approved_with_conditions",

  /**
   * Represents an internal status used when applications are merged (e.g., donor applications) and are no longer visible.
   */
  Merged = "merged",

  /**
   * Represents partially submitted applications where some applicants have completed their submissions, but others are still working on their verifications.
   */
  PartiallySubmitted = "partially_submitted",

  /**
   * Represent status that submitted application can be transitioned to. This block reports and further actions in applications.
   * Kinda like freeze and save for later
   */
  OnHold = "on_hold"
}

export type DiscountType = "percentage" | "flat" | "free";

export interface AppliedDiscountInfo {
  discount_amount?: Money | number;
  discount_type?: DiscountType;
  discount_code?: string;
  applied_by?: string;
  applied_at?: string;
  is_custom?: boolean;
  end_date?: string;
  discount_applied?: boolean;
  applied_by_user_name?: string;
}

export interface Invitee {
  used?: boolean;
  role: ApplicantType;
  application_fee_status: ApplicationFeeStatus;
  discount_info?: AppliedDiscountInfo;
  id: number;
  phone?: string;
  active: boolean;
  application_status: ApplicantStatus;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  is_public?: boolean;
  criteria_check_settings: unknown;
  unit_id: number;
  application_id: number;
  kind: string;
  invited_by_applicant_id?: number;
  email: string;
  application_link?: string;
}

export enum ScreeningStatus {
  NotStarted = "not_started",
  Processing = "processing",
  Completed = "completed",
  Failed = "failed"
}

export enum ApplicantStatus {
  Pending = "pending",
  Started = "started",
  Submitted = "submitted",
  Finished = "finished",
  Canceled = "canceled",
  Processing = "processing",
  OnHold = "on_hold"
}

export enum ApplicantType {
  Applicant = "applicant",
  CoSigner = "co_signer",
  Occupant = "occupant"
}

export enum Requirements {
  HousingHistory = "housing_history_more_than_2_years"
}

export interface Requirement {
  requirement: Requirements;
}

export enum IneligibleCheckoutReason {
  UnitDelisted = "unit_delisted",
  ApplicationFailed = "application_failed",
  ApplicationIsNotFinished = "application_is_not_finished",
  GroupApplicationCanNotBeSubmitted = "group_application_can_not_be_submitted",
  RequirementsNotMet = "requirements_not_met"
}

export enum ApplicationFeeStatus {
  Paid = "paid",
  Refunded = "refunded",
  Unpaid = "unpaid",
  NoApplicationFee = "no application fee"
}

export enum CriteriaRentType {
  Both = "both",
  Home = "home",
  Site = "site"
}

export interface Money {
  cents: number;
  currency: string;
}

export interface DiscountInfo {
  discount_applied: boolean;
  discount_amount: Money;
  discount_code: string;
  final_amount: Money;
  original_amount: Money;
}

export interface Applicant {
  role: null | ApplicantType;
  id: number;
  middle_name?: string;
  application_started_at: Date | null;
  application_finished_at: Date | null;
  // This is actually ApplicantStatus. Not ApplicationStatus.
  // No mistake here. Go on.
  application_status: ApplicantStatus;
  fee: {
    cents: number;
    currency: string;
  };
  can_checkout?: boolean;
  requirements?: Requirement[];
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  verifications?: Verification[];
  intercom_id?: string | number;
  application_id: number;
  criteria_check_settings?: CriteriaCheckSettings;
  template_criteria_settings?: CoSignerSettings | ApplicantSettings;
  retry_identity_verification_status?: RetryIdentityVerificationStatus;
  pending_magic_links: {
    email: string;
    phone: string | null;
    role: string;
  }[];
  criteria?: { score: number }[];
  refunded?: boolean;
  ineligible_to_checkout_reason?: IneligibleCheckoutReason;
  stream_chat_id?: string;
  stream_chat_token?: string;
  stream_chat_name?: string;
  disclosures?: string;
  application_fee_status: ApplicationFeeStatus;
  paid_application_fee: Money | null;
  refunded_application_fee: Money | null;
  application_fee: Money | null;
  housing_addresses?: {
    full_address: string;
    current_residence: null | boolean;
    zip: string;
    city: string;
    state: string;
    county?: string;
    address1: string;
    fips_data?: {
      fips: string;
      name: string;
      price: Money;
      surcharge: Money;
      total: Money;
      base_markup_percentage: null;
      base_markup_amount: null;
      surcharge_markup_percentage: null;
      surcharge_markup_amount: null;
    };
  }[];

  // PX only
  is_manual?: boolean;
  // PX only
  manual_email?: string;
  // PX only
  preferred_checkout_type?: CheckoutSubmissionType;
  // PX only
  preferred_submission_disclosure_type?: PreferredSubmissionType;
  // PX only
  has_plaid_data?: boolean;
  // AX only
  invited_by_applicant_id?: number;
  has_passed_invite_flow: boolean;
}

export enum SubmissionType {
  InPerson = "in_person",
  SendByEmail = "send_by_email",
  SignOnBehalf = "sign_on_behalf",
  CheckoutPaidInCash = "checkout_payment_in_cash"
}

export type CheckoutSubmissionType =
  | SubmissionType.InPerson
  | SubmissionType.SendByEmail
  | SubmissionType.CheckoutPaidInCash;

export type PreferredSubmissionTypeSubmissionDisclosureType =
  | SubmissionType.InPerson
  | SubmissionType.SendByEmail
  | SubmissionType.SignOnBehalf;

export type PreferredSubmissionType =
  | CheckoutSubmissionType
  | PreferredSubmissionTypeSubmissionDisclosureType;

interface CriteriaCheckSettings {
  bankruptcy_records: CriteriaCheckSetting;
  credit_score: CriteriaCheckSetting;
  gross_income: CriteriaCheckSetting;
  gross_income_to_rent_ratio?: CriteriaCheckSetting;
  housing_court_records?: CriteriaCheckSetting;
  net_income: CriteriaCheckSetting;
  past_due_account: CriteriaCheckSetting;
  sex_offender_check: CriteriaCheckSetting;
  unpaid_collections_balance: CriteriaCheckSetting;
  income_document_fraud_check: CriteriaCheckSetting;
}

interface CoSignerSettings {
  bankruptcy_records?: SettingsType & { years: string };
  credit_score?: SettingsType & { type: string };
  gross_income?: SettingsType & { type: string };
  gross_income_to_rent_ratio?: SettingsType;
  housing_court_records?: SettingsType & {
    housing_court_records_years: number;
  };
  net_income?: SettingsType & { type: string };
  past_due_account?: SettingsType & {
    past_due_account_rate: string;
  };
  unpaid_collections_balance?: SettingsType;
  collections_balance?: SettingsType;
}

export type ApplicantSettings = CoSignerSettings & {
  sex_offender_check?: SettingsType;
  income_document_fraud_check?: SettingsType;
};

interface CriteriaCheckSetting {
  value: number | string | null;
  check: number;
}

interface SettingsType {
  value: number;
  importance?: number | null;
}

export enum DecisionAction {
  Approve = "approve",
  ConditionalApprove = "conditional_approve",
  Decline = "decline"
}

interface AllowRole {
  applicant: boolean;
  co_signer: boolean;
  occupant: boolean;
}

export interface IdentityVerificationSettings {
  identity_verification?: {
    selfie_check: boolean;
    prevent_apply_if_failed?: boolean;
    provider?: string;
  };
  enabled_screening_services?: boolean;
}

export interface RequiresOverridesIdentityVerification {
  applicants: {
    id: number;
    name: string;
    role: ApplicantType;
  }[];
}

export enum RetryIdentityVerificationStatus {
  NotAvailable = "not_available",
  Canceled = "canceled",
  Pending = "pending",
  Completed = "completed",
  InProgress = "in_progress"
}

export interface Application {
  id: number;
  desired_move_in_date?: string;
  partner: Partner & {
    is_sites_and_communities?: boolean;
    google_analytics_id?: string | string[];
    is_home_site_rent?: boolean;
  };
  unit: Unit & {
    listed: boolean;
    magic_link: {
      id: number;
      active: boolean;
    };
  };
  status: ApplicationStatus;
  property?: Property;
  email?: string;
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  base_rent_amount: Money | null;
  applicants: (Applicant & { is_manual: boolean })[];
  current_applicant?: Applicant;
  verifications?: Verification[];
  requires_manual_or_conditional_execution?: {
    applicants?: {
      id: number;
      name: string;
      role: ApplicantType;
      verifications: {
        execution_mode: ExecutionModes;
        id: number;
        name: VerificationNames;
      }[];
    }[];
    total_verifications?: number;
  };
  progress: {
    total: number;
    finished: number;
  };
  recommendation?: DecisionAction;
  pending_magic_links: Invitee[];
  verification_template: {
    id?: string;
    allow_role: AllowRole;
    has_move_in_date_limit: boolean;
    max_move_in_days: number | null;
    criteria_rent_type?: CriteriaRentType;
    settings: Record<ApplicantType, IdentityVerificationSettings>;
    verification_schemas?: Record<
      ApplicantType,
      {
        [VerificationNames.HousingHistory]?: {
          min_required_continuous_rental_history: number;
        };
      }
    >;
  };
  eligible_for_role_update: boolean;
  has_pending_verifications: boolean;
  requires_overrides_identity_verification: RequiresOverridesIdentityVerification;
  decision_at?: string;
  partner_name_in_submission_disclosure: string;
  has_multiple_applicants?: boolean;
  allow_occupants?: boolean;
  has_multiple_guarantors?: boolean;
  conditional_approval_email_message_rendered?: string | null;
}

export interface SubmissionDisclosureLink {
  id: number;
  email: string;
  status: string;
  subject_id: number;
  subject_type: string;
  token: string;
  type: string;
  expires_at: string | null;
  metadata: string;
  created_at: string;
  updated_at: string;
}

export interface ApplicationRent {
  monthly_rent_amount: Money;
  base_rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
  criteria_rent_type?: CriteriaRentType;
}
